import { theme } from '@/constants';
import { useImageFallback } from '../useImageFallback.hook';
import { TileImage, TileInitial, TileWrapper } from './LogoTile.styles';

export interface LogoTileProps {
  /** Carrier or merchant name — also the source of the fallback initial. */
  name: string;
  logoUrl?: string;
  /** Edge length in px. 78 in the leaderboards, 80 in the carrier header. */
  size?: number;
  /** The design rounds the card-grid tiles barely at all and the carrier header tile noticeably. */
  radius?: string;
  className?: string;
}

const DEFAULT_SIZE = 78;
const DEFAULT_RADIUS = theme.spacing[2];

/**
 * Square logo tile with a text fallback.
 *
 * A missing logo is ordinary, not exceptional: a long tail of carriers and
 * merchants have none, and a broken image is indistinguishable from one that was never supplied.
 * Both paths land on the same initial-letter tile so a row never collapses or renders an empty box.
 */
const LogoTile: React.FC<LogoTileProps> = ({
  name,
  logoUrl,
  size = DEFAULT_SIZE,
  radius = DEFAULT_RADIUS,
  className,
}) => {
  const { ref, hasFailed, onError } = useImageFallback(logoUrl);
  const showImage = !!logoUrl && !hasFailed;

  return (
    <TileWrapper $size={size} $radius={radius} $filled={!showImage} className={className}>
      {showImage ? (
        <TileImage ref={ref} src={logoUrl} alt={name} loading='lazy' onError={onError} />
      ) : (
        <TileInitial aria-hidden>{name.trim().charAt(0).toUpperCase()}</TileInitial>
      )}
    </TileWrapper>
  );
};

export default LogoTile;
